import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { appointmentsApi } from './appointmentsApi';
import type {
  AppointmentListParams,
  AppointmentPayload,
  AppointmentUpdatePayload,
  AvailabilityParams,
  CalendarParams,
  ReschedulePayload,
  StatusPayload,
} from './types';

const KEY = 'appointments';
const CLIENTS_KEY = 'clients';

/**
 * Invalidates everything an appointment write can have changed.
 *
 * The calendar, the client's history and her visit counters are three views of
 * the same rows: completing an appointment moves her `totalVisits` without any
 * client row changing, and leaving that cache alone shows a profile that
 * disagrees with the agenda next to it.
 */
const useInvalidateAgenda = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY] });
    void qc.invalidateQueries({ queryKey: [CLIENTS_KEY] });
    // Inicio's figures count completed visits.
    void qc.invalidateQueries({ queryKey: ['insights'] });
  };
};

export const useCalendar = (params: CalendarParams) =>
  useQuery({
    queryKey: [KEY, 'calendar', params],
    queryFn: () => appointmentsApi.calendar(params),
    // Every step of the date and every switch of view is a new key, and so a
    // fresh fetch. Without this the grid is replaced by a spinner on the most
    // frequent interaction this screen has; keeping the previous range on
    // screen while the next one loads makes it read as navigation instead of
    // as a reload.
    placeholderData: (previous) => previous,
  });

export const useAppointmentList = (params?: AppointmentListParams) =>
  useQuery({
    queryKey: [KEY, 'list', params],
    queryFn: () => appointmentsApi.list(params),
  });

export const useAppointmentEvents = (id: number | null) =>
  useQuery({
    queryKey: [KEY, 'events', id],
    queryFn: () => appointmentsApi.events(id as number),
    enabled: id !== null && Number.isFinite(id) && id > 0,
  });

/**
 * Free slots for a date and a set of services.
 *
 * Disabled until something is actually being booked: asking for availability
 * with no services would be asking how long nothing takes.
 */
export const useAvailability = (params: AvailabilityParams | null) =>
  useQuery({
    queryKey: [KEY, 'availability', params],
    queryFn: () => appointmentsApi.availability(params as AvailabilityParams),
    enabled: params !== null && params.serviceIds.length > 0,
    // Slots go stale the moment somebody else books one, and this list is read
    // seconds before a write that depends on it.
    staleTime: 0,
  });

export const useCreateAppointment = () => {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: (data: AppointmentPayload) => appointmentsApi.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateAppointment = () => {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AppointmentUpdatePayload }) =>
      appointmentsApi.update(id, data),
    onSuccess: invalidate,
  });
};

export const useRescheduleAppointment = () => {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ReschedulePayload }) =>
      appointmentsApi.reschedule(id, data),
    onSuccess: invalidate,
  });
};

export const useChangeAppointmentStatus = () => {
  const invalidate = useInvalidateAgenda();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StatusPayload }) =>
      appointmentsApi.changeStatus(id, data),
    onSuccess: invalidate,
  });
};
